import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { server } from '../api';

// Passive stream state hook for dashboard cards.
// Polls /v1/streaming/state/{cameraId} without registering a viewer slot —
// no viewer/start or viewer/stop, suitable for read-only display.
const POLL_MS = 10000;
const GB28181_PROTOCOL = 'gb28181';

const probeHls = async (url) => {
  try {
    await axios.get(url, { timeout: 3000, validateStatus: (s) => s === 200 });
    return true;
  } catch {
    return false;
  }
};

const useStreamState = (cameraId) => {
  const [posterUrl, setPosterUrl] = useState(null);
  const [posterLoaded, setPosterLoaded] = useState(false);
  const [posterBust, setPosterBust] = useState(0);
  const [streamStatus, setStreamStatus] = useState('loading'); // 'loading' | 'active' | 'offline'
  const [hlsUrl, setHlsUrl] = useState(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const resolve = useCallback(async (data) => {
    if (!isMounted.current) return;
    setPosterUrl(data.last_frame_url || null);

    if (data.active === 0) {
      setHlsUrl(null);
      setStreamStatus('offline');
      return;
    }
    if (data.live === 'streaming' && data.hls_url) {
      setHlsUrl(data.hls_url);
      setStreamStatus('active');
      return;
    }
    // GB28181: backend `live` is authoritative, and probing the playlist makes
    // WVP auto-start an unwatched camera. Only RTMP/P2P `live` lags and needs a probe.
    if (data.hls_url && data.protocol !== GB28181_PROTOCOL) {
      const ok = await probeHls(data.hls_url);
      if (!isMounted.current) return;
      if (ok) {
        setHlsUrl(data.hls_url);
        setStreamStatus('active');
        return;
      }
    }
    setHlsUrl(null);
    setStreamStatus('offline');
  }, []);

  useEffect(() => {
    if (!cameraId) {
      setStreamStatus('offline');
      setHlsUrl(null);
      setPosterUrl(null);
      setPosterLoaded(false);
      return;
    }
    setPosterBust((n) => n + 1);
    setPosterLoaded(false);
    setStreamStatus('loading');
    let cancelled = false;

    const fetchState = async () => {
      try {
        const { data } = await axios.get(`${server}v1/streaming/state/${cameraId}`);
        if (!cancelled && isMounted.current) await resolve(data);
      } catch {
        if (!cancelled && isMounted.current) setStreamStatus('offline');
      }
    };

    fetchState();
    const id = setInterval(fetchState, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [cameraId, resolve]);

  return { posterUrl, posterLoaded, setPosterLoaded, posterBust, streamStatus, hlsUrl };
};

export default useStreamState;
